import { useEffect, useState } from 'react'
import { client, urlFor } from '../client'
import type { SanityImageSource } from '@sanity/image-url'

const BRAND_QUERY = `*[_type == "brandSettings"][0] {
  primaryColor,
  secondaryColor,
  logo
}`

export const BRAND_STORAGE_KEY = 'brand-settings'

type BrandSettings = {
  primaryColor?: string
  secondaryColor?: string
  logo?: SanityImageSource
}

function adjust(hex: string, factor: number): string {
  const n = hex.replace('#', '')
  const r = Math.round(Math.max(0, Math.min(255, parseInt(n.slice(0, 2), 16) * factor)))
  const g = Math.round(Math.max(0, Math.min(255, parseInt(n.slice(2, 4), 16) * factor)))
  const b = Math.round(Math.max(0, Math.min(255, parseInt(n.slice(4, 6), 16) * factor)))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

function mixWhite(hex: string, amount: number): string {
  const n = hex.replace('#', '')
  const blend = (v: number) => Math.round(v + (255 - v) * amount)
  const r = blend(parseInt(n.slice(0, 2), 16))
  const g = blend(parseInt(n.slice(2, 4), 16))
  const b = blend(parseInt(n.slice(4, 6), 16))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

export function applyBrandVars(primaryColor?: string, secondaryColor?: string, prefix = 'ap') {
  const root = document.documentElement
  const p = prefix

  if (primaryColor) {
    root.style.setProperty(`--${p}-color-primary`, primaryColor)
    root.style.setProperty(`--${p}-color-primary-hover`, adjust(primaryColor, 0.83))
    root.style.setProperty(`--${p}-color-primary-light`, mixWhite(primaryColor, 0.7))
    root.style.setProperty(`--${p}-color-primary-dark`, adjust(primaryColor, 0.6))
    root.style.setProperty(`--${p}-color-accent`, primaryColor)
    root.style.setProperty(`--${p}-color-accent-hover`, adjust(primaryColor, 0.83))
    root.style.setProperty(`--${p}-color-accent-light`, mixWhite(primaryColor, 0.7))
    root.style.setProperty(`--${p}-color-accent-dark`, adjust(primaryColor, 0.6))
    root.style.setProperty(`--${p}-color-content-interactive`, primaryColor)
    root.style.setProperty(`--${p}-color-content-interactive-hover`, adjust(primaryColor, 0.83))
    root.style.setProperty(`--${p}-color-content-button`, adjust(primaryColor, 0.4))
    root.style.setProperty(`--${p}-color-stroke-accent`, primaryColor)
    root.style.setProperty(`--${p}-color-surface-accent`, mixWhite(primaryColor, 0.95))
    root.style.setProperty(`--${p}-color-surface-accent-hover`, mixWhite(primaryColor, 0.9))
    root.style.setProperty(`--${p}-color-surface-accent-dark`, mixWhite(primaryColor, 0.8))
    root.style.setProperty(`--${p}-color-surface-accent-dark-hover`, mixWhite(primaryColor, 0.7))
  }

  if (secondaryColor) {
    root.style.setProperty(`--${p}-color-secondary`, secondaryColor)
    root.style.setProperty(`--${p}-color-secondary-hover`, adjust(secondaryColor, 0.83))
    root.style.setProperty(`--${p}-color-secondary-light`, mixWhite(secondaryColor, 0.9))
    root.style.setProperty(`--${p}-color-secondary-dark`, adjust(secondaryColor, 0.6))
    root.style.setProperty(`--${p}-color-content-accent`, secondaryColor)
    root.style.setProperty(`--${p}-color-content-accent-hover`, adjust(secondaryColor, 0.83))
  }
}

export function useBrandSettings() {
  const [settings, setSettings] = useState<BrandSettings | null>(() => {
    try {
      const stored = localStorage.getItem(BRAND_STORAGE_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    client.fetch<BrandSettings>(BRAND_QUERY).then((data) => {
      if (data) {
        localStorage.setItem(
          BRAND_STORAGE_KEY,
          JSON.stringify({
            primaryColor: data.primaryColor,
            secondaryColor: data.secondaryColor,
          }),
        )
      }
      setSettings(data)
    })
  }, [])

  useEffect(() => {
    applyBrandVars(settings?.primaryColor, settings?.secondaryColor)
  }, [settings])

  const logoUrl = settings?.logo ? (urlFor(settings.logo)?.height(64).url() ?? undefined) : undefined

  return { logoUrl }
}
